import type { MutationPrice } from 'generated/gql';
import type { Spacings } from '@rulebooks/evidend-titan';
import { InformationIcon, StatusMessage } from '@rulebooks/evidend-titan';
import { t, Trans } from '@lingui/macro';
import { formatCurrency } from 'helpers/money';
import { useAccount } from 'hooks/useAccount';

/** Drafting, editing and cancelling are free; only a price without exemption is charged. */
export const isChargedPrice = (price?: MutationPrice | null): boolean =>
  !!price && !price.exemption && price.amountEuros > 0;

export const formatFeeAmount = (amountEuros: number) => formatCurrency('EUR', 2, amountEuros);

// Without a group there is no account name to bill to, so the sentence stops at the fee.
const formatFeeSentence = (price: MutationPrice, groupName?: string) => {
  if (!isChargedPrice(price)) {
    return groupName ? (
      <Trans>
        De kosten voor deze transactie zijn <strong>gratis</strong> en worden in rekening gebracht
        op het account van <strong>{groupName}</strong>.
      </Trans>
    ) : (
      <Trans>
        De kosten voor deze transactie zijn <strong>gratis</strong>.
      </Trans>
    );
  }

  const fee = formatFeeAmount(price.amountEuros);
  return groupName ? (
    <Trans>
      De kosten voor deze transactie zijn <strong>{fee}</strong> en worden in rekening gebracht op
      het account van <strong>{groupName}</strong>.
    </Trans>
  ) : (
    <Trans>
      De kosten voor deze transactie zijn <strong>{fee}</strong>.
    </Trans>
  );
};

const formatFeeMessage = (price: MutationPrice, groupName?: string) => (
  <>
    {t`Controleer de gegevens. Klik op Bevestigen als de gegevens van deze transactie kloppen.`}{' '}
    {formatFeeSentence(price, groupName)}
  </>
);

/**
 * The notice's text, for places that render it as plain text instead of a status message.
 * Undefined while the price is unknown, mirroring the notice rendering nothing.
 */
export const useMutationFeeMessage = (price?: MutationPrice | null) => {
  const account = useAccount();

  return price ? formatFeeMessage(price, account?.group?.name) : undefined;
};

interface MutationFeeNoticeProps {
  price?: MutationPrice | null;
  isLoading?: boolean;
  spaceAfter?: Spacings;
}

/**
 * The "this costs € x" line shown above the button that finalises a mutation. Renders nothing
 * while the price is unknown, so a failing price query never blocks the mutation itself.
 */
export const MutationFeeNotice = ({ price, isLoading, spaceAfter }: MutationFeeNoticeProps) => {
  const message = useMutationFeeMessage(price);

  if (isLoading || !message) return null;

  return (
    <StatusMessage
      icon={<InformationIcon />}
      spaceAfter={spaceAfter}
      testId="mutationFeeNotice"
      type="default"
    >
      {message}
    </StatusMessage>
  );
};
